<template>
  <main class="home">
    <LightsImage />


    <UserInfoSection />
    <GameSection
      @start:newGame="launchNewGameFlow"
      @redirect:storePage="redirectToStorePage"
    />

    <PrizesCarousel />

    <template v-if="hasInviteFunctionality">
      <BaseInviteItem @invite="openInviteModal" />

      <InviteModal v-if="isInviteModalOpen" @invite:resolved="closeInviteModal" />
    </template>

    <section class="bonus_phases">
      <BonusPhaseListItem
        v-for="bonusPhase in filteredBonusPhaseList"
        :key="bonusPhase.id"
        :bonus-phase="bonusPhase"
        @select:bonusPhase="handleBonusPhaseSelect"
      />
    </section>
  </main>

  <PreGameSection
    v-if="isPreGameVisible"
    @hide:preGame="hidePreGame"
  />

  <CompletedLevelModal
    v-else-if="isLevelUpModalOpen"
    :data="levelUpData"
    @redirect:levelsPage="handleRedirectToLevelsPageFromLevelModal"
  />

  <BonusPhaseDailyModal
    v-else-if="isBonusPhaseDailyModalOpen"
    :prize-value="bonusPhaseDailyItem.prizeValue"
    :prize-type="bonusPhaseDailyItem.prizeType"
    :ignore-redirection="true"
  />

  <BonusPhaseGameModal
    v-else-if="isBonusPhaseGameModalOpen"
    :can-play="bonusPhaseGameItem.canPlay"
    @start:bonusGame="handleStartBonusGameFromBonusPhaseGameModal"
  />

  <HomePhoneShortcutModal
    v-else-if="isPhoneShortcutModalOpen"
    @close="handlePhoneShortcutModalClose"
    @ok="handlePhoneShortcutModalOk"
  />

  <PreGamePuzzleModal
    v-else-if="isPreGamePuzzleModalOpen"
    @start:puzzleGame="handleStartPuzzleGame"
  />

  <PreGameLanternModal
    v-else-if="isPreGameLanternModalOpen"
    @start:lanternGame="handleStartLanternGame"
  />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import LightsImage from '@/components/image/LightsImage.vue';
import InviteModal from '@/components/modal/InviteModal.vue';
import BaseInviteItem from '@/components/item/BaseInviteItem.vue';
import PrizesCarousel from '@/components/carousel/PrizesCarousel.vue';
import BonusPhaseListItem from '@/views/home/components/item/BonusPhaseListItem.vue';
import GameSection from '@/views/home/components/section/GameSection.vue';
import UserInfoSection from '@/views/home/components/section/UserInfoSection.vue';
import PreGameSection from '@/components/section/PreGameSection.vue';
import CompletedLevelModal from '@/components/modal/level/CompletedLevelModal.vue';
import BonusPhaseDailyModal from '@/components/modal/BonusPhaseDailyModal.vue';
import BonusPhaseGameModal from '@/components/modal/BonusPhaseGameModal.vue';
import HomePhoneShortcutModal from '@/components/modal/HomePhoneShortcutModal.vue';
import PreGamePuzzleModal from '@/views/bonusGame/components/modal/PreGamePuzzleModal.vue';
import PreGameLanternModal from '@/views/bonusGame/components/modal/PreGameLanternModal.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useBonusPhaseStore from '@/store/modules/bonusPhase.js';
import useAuthStore from '@/store/modules/auth.js';
import usePhoneShortcutStore from '@/store/modules/phoneShortcut.js';
import useDeviceHook from '@/hooks/useDevice.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useModalHook from '@/hooks/useModal.js';
import usePreGameHook from '@/hooks/usePreGame.js';
import { apiGetBonusGameStatus } from '@/services/game.js';
import { bonusPhaseTypeEnum } from '@/constants/bonusPhase.js';

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();
const bonusPhaseStore = useBonusPhaseStore();
const phoneShortcutStore = usePhoneShortcutStore();

const { isMobileDevice } = useDeviceHook();
const { redirectTo, redirectToStorePage } = usePageNavigationHook();
const [isBonusPhaseDailyModalOpen, openBonusPhaseDailyModal] = useModalHook();
const [isBonusPhaseGameModalOpen, openBonusPhaseGameModal, closeBonusPhaseGameModal] = useModalHook();
const [isLevelUpModalOpen, openLevelUpModal, closeLevelModal] = useModalHook();
const [isInviteModalOpen, openInviteModal, closeInviteModal] = useModalHook();
const [isPhoneShortcutModalOpen, openPhoneShortcutModal, closePhoneShortcutModal] = useModalHook();
const [isPreGamePuzzleModalOpen, openPreGamePuzzleModal, closePreGamePuzzleModal] = useModalHook();
const [isPreGameLanternModalOpen, openPreGameLanternModal, closePreGameLanternModal] = useModalHook();
const { isPreGameVisible, launchNewGameFlow, hidePreGame } = usePreGameHook();

const modalsChainConfiguration = ref();
const bonusPhaseDailyItem = ref(null);
const bonusPhaseGameItem = ref(null);
const levelUpData = ref({});
const hasInviteFunctionality = appSettingsStore.appProperties.inviteConfig.inviteEnabled;
const filteredBonusPhaseList = computed(() =>
  bonusPhaseStore.bonusPhaseList.filter(({ __type }) => (__type === bonusPhaseTypeEnum.POINTS) ||
    ((__type === bonusPhaseTypeEnum.BUNDLES) && !appSettingsStore.isStoreDisabled),
  ),
);

const handleBonusPhaseSelect = ({ bonusPhaseType }) => {
  switch (bonusPhaseType) {
    case bonusPhaseTypeEnum.BUNDLES:
      redirectToStorePage();
      break;
    case bonusPhaseTypeEnum.POINTS:
      launchNewGameFlow();
      break;
    default:
      break;
  }
};

const handleRedirectToLevelsPageFromLevelModal = async () => {
  await closeLevelModal({ immediate: true });

  redirectTo('Levels');
};

const handleStartBonusGameFromBonusPhaseGameModal = async () => {
  await closeBonusPhaseGameModal({ immediate: true });

  const modalArgs = {
    showImmediate: true,
    afterHideCallback: () => {
      bonusPhaseStore.resetBonusPhaseGameConfigState();

      modalsChainConfiguration.value.next();
    },
  };

  if (bonusPhaseStore.bonusPhasePuzzleGame) {
    bonusPhaseStore.setBonusPhaseGameConfig({
      gameId: bonusPhaseStore.bonusPhasePuzzleGame.id,
      prizeValue: bonusPhaseStore.bonusPhasePuzzleGame.rewardPoints,
    });

    openPreGamePuzzleModal(modalArgs);
  } else if (bonusPhaseStore.bonusPhaseLanternGame) {
    bonusPhaseStore.setBonusPhaseGameConfig({
      gameId: bonusPhaseStore.bonusPhaseLanternGame.id,
      prizeValue: bonusPhaseStore.bonusPhaseLanternGame.rewardPoints,
    });

    openPreGameLanternModal(modalArgs);
  } else {
    modalsChainConfiguration.value.next();
  }
};

// ✅ ADDED: Phone shortcut modal event handlers
const handlePhoneShortcutModalClose = async () => {
  await closePhoneShortcutModal({ immediate: true });
};

const handlePhoneShortcutModalOk = async () => {
  await closePhoneShortcutModal({ immediate: true });
};

const handleStartPuzzleGame = async () => {
  await closePreGamePuzzleModal({ immediate: true });

  redirectTo('BonusGamePuzzle');
};

const handleStartLanternGame = async () => {
  await closePreGameLanternModal({ immediate: true });

  redirectTo('BonusGameLantern');
};

const checkAndOpenBonusPhaseGameModal = async () => {
  if (
    !bonusPhaseStore.isBonusPhaseGameModalShown &&
    (bonusPhaseStore.bonusPhasePuzzleGame || bonusPhaseStore.bonusPhaseLanternGame)
  ) {
    bonusPhaseStore.isBonusPhaseGameModalShown = true;

    let gameId = '';
    if (bonusPhaseStore.bonusPhasePuzzleGame) {
      gameId = bonusPhaseStore.bonusPhasePuzzleGame.id;
    } else if (bonusPhaseStore.bonusPhaseLanternGame) {
      gameId = bonusPhaseStore.bonusPhaseLanternGame.id;
    }

    try {
      const { canPlay } = await apiGetBonusGameStatus({ queryParams: { gameId } });

      bonusPhaseGameItem.value = { canPlay };

      openBonusPhaseGameModal({
        afterHideCallback: () => {
          modalsChainConfiguration.value.next();
        },
      });
    } catch {
      modalsChainConfiguration.value.next();
    }
  } else {
    modalsChainConfiguration.value.next();
  }
};

const checkAndOpenBonusPhaseDailyModal = () => {
  const dailyBonusPhase = bonusPhaseStore.bonusPhaseList.find(({ __type }) => __type === bonusPhaseTypeEnum.DAILY);

  if (!bonusPhaseStore.isBonusPhaseDailyModalShown && dailyBonusPhase) {
    bonusPhaseStore.isBonusPhaseDailyModalShown = true;

    bonusPhaseDailyItem.value = {
      prizeValue: dailyBonusPhase.quantity,
      prizeType: dailyBonusPhase.dailyBonusType,
    };

    openBonusPhaseDailyModal({
      afterHideCallback: () => {
        modalsChainConfiguration.value.next();
      },
    });
  } else {
    modalsChainConfiguration.value.next();
  }
};

const checkAndOpenLevelUpModal = () => {
  if (authStore.levelUp) {
    levelUpData.value.__level = authStore.levelUp.level;
    levelUpData.value.prizeValue = authStore.levelUp.prizeValue;
    authStore.setLevelUp(null);

    openLevelUpModal({
      afterHideCallback: () => {
        levelUpData.value = {};
        modalsChainConfiguration.value.next();
      },
    });
  } else {
    modalsChainConfiguration.value.next();
  }
};

const checkAndOpenPhoneShortcutModal = () => {
  console.log('🔍 Checking phone shortcut modal conditions...');

  const shouldShowPhoneShortcut = () => {
    console.log('📱 Evaluating shouldShowPhoneShortcut...');

    // For testing purposes, let's enable detailed logging
    const isPhoneShortcutEnabled = appSettingsStore.appProperties.phoneShortcutEnabled;
    const homeScreenModalApplied = localStorage.getItem('homeScreenModalApplied');
    const homeScreenModalDeniedForSession = sessionStorage.getItem('homeScreenModalDenied');
    const homeScreenModalDenied = localStorage.getItem('homeScreenModalDenied');
    const hasInstallPrompt = phoneShortcutStore.getInstallPromptEvent();

    const isShortcutModalNotEnabled = !isMobileDevice || !isPhoneShortcutEnabled ||
      (homeScreenModalApplied === 'true') || (homeScreenModalDenied === 'true') || (homeScreenModalDeniedForSession === 'true');

    // Check if shortcut modal should NOT be shown
    if (isShortcutModalNotEnabled) {
      console.log('  - Is Mobile:', isMobileDevice);
      console.log('  - Phone shortcut enabled:', isPhoneShortcutEnabled);
      console.log('  - Modal already applied:', homeScreenModalApplied);
      console.log('  - Modal denied this session:', homeScreenModalDeniedForSession);
      console.log('  - Modal denied in general:', homeScreenModalDenied);
      console.log('  - Has install prompt:', !!hasInstallPrompt);
      console.log('  - User agent:', navigator.userAgent);
      return false;
    }
    // Load phone shortcut manifest
    phoneShortcutStore.loadManifest();

    console.log('✅ All conditions met - showing phone shortcut modal');
    return true;
  };

  if (shouldShowPhoneShortcut()) {
    console.log('🚀 Opening phone shortcut modal');
    openPhoneShortcutModal({
      hideManually: true,
      afterHideCallback: () => {
        console.log('📱 Phone shortcut modal closed, continuing chain...');
        modalsChainConfiguration.value.next();
      },
    });
  } else {
    console.log('⏭️ Skipping phone shortcut modal, continuing chain...');
    modalsChainConfiguration.value.next();
  }
};

// Also add this to your onMounted to enable phone shortcut for testing:
onMounted(() => {
  modalsChainConfiguration.value = {
    index: 0,
    methodList: [
      checkAndOpenLevelUpModal,
      checkAndOpenBonusPhaseDailyModal,
      checkAndOpenBonusPhaseGameModal,
      checkAndOpenPhoneShortcutModal,
    ],
    init: function () {
      console.log('🎬 Starting modals chain...');
      this.methodList[0](this);
    },
    next: function () {
      if (this.index < (this.methodList.length - 1)) {
        console.log(`⏭️ Modal chain: moving to step ${ this.index + 1 }`);
        this.methodList[++this.index](this);
      } else {
        console.log('🏁 Modal chain completed');
      }
    },
  };

  modalsChainConfiguration.value.init();
});
</script>
